import Decimal from 'decimal.js';
import { noop } from 'lodash';
import { RefObject, useEffect, useMemo, useRef } from 'react';
import { SetRequired } from 'type-fest';

import {
  FullScreenModal,
  FullScreenModalProps,
} from '@/components/modals/FullScreenModal/FullScreenModal';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { SelectedSuggestionEvidenceProvider } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.provider';
import { SuggestionAcknowledgementProvider } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.provider';
import { TestamentaryEntitySuggestionsProvider } from '@/modules/aiSuggestions/context/TestamentaryEntitySuggestions.provider';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { DocumentChatPane } from '@/modules/chat/components/DocumentChatPane';
import {
  DocumentChatProvider,
  useOptionalDocumentChatContext,
} from '@/modules/chat/contexts/DocumentChatProvider';
import { DispositiveDiagramSyncProvider } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { DPFormRecipientTrackerProvider } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.provider';
import { useResetEvidenceOnDPEntry } from '@/modules/dispositiveProvisions/hooks/useResetEvidenceOnDPEntry';
import { SplitScreenLayoutWithDocuments } from '@/modules/documents/components/SplitScreenLayoutWithDocuments/SplitScreenLayoutWithDocuments';
import { TestamentaryEntityDetailsContext } from '@/modules/entities/contexts/testamentaryEntityDetails/testamentaryEntityDetails.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useOptionalAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';

import { useEditTestamentaryEntitySplitScreenModalContext } from './contexts/editTestamentaryEntitySplitScreenModal.context';
import { EditTestamentaryEntitySplitScreenModalProvider } from './contexts/EditTestamentaryEntitySplitScreenModal.provider';
import { EditTestamentaryEntitySection } from './EditTestamentaryEntitySplitScreen.types';
import { EditTestamentaryEntitySplitScreenFooter } from './EditTestamentaryEntitySplitScreenFooter';
import { EditTestamentaryEntitySplitScreenHeader } from './EditTestamentaryEntitySplitScreenHeader';
import { EditTestamentaryEntitySplitScreenRightContent } from './EditTestamentaryEntitySplitScreenRightContent';

export interface EditTestamentaryEntitySplitScreenModalProps
  extends EditTestamentaryEntitySplitScreenModalInnerProps {
  testamentaryEntityId: string;
  isTwoClientHousehold: boolean;
  initialSection: EditTestamentaryEntitySection;
  /**
   * Optional initial value for the first death client ID in the dispositions section.
   * When provided (e.g., from an estate waterfall context where we know the death order),
   * the dispositions section will default to showing this client's death scenario.
   */
  initialFirstDeathClientId?: string | null;
}

type EditTestamentaryEntitySplitScreenModalInnerProps = SetRequired<
  Partial<FullScreenModalProps>,
  'onClose'
>;

function EditTestamentaryEntitySplitScreenModalInner(
  modalProps: EditTestamentaryEntitySplitScreenModalInnerProps
) {
  const { householdId, primaryClients } = useHouseholdDetailsContext();
  const { testamentaryEntityId, isDPSection, selectedFirstDeathClientId } =
    useEditTestamentaryEntitySplitScreenModalContext();

  const {
    documentPanelProps: {
      activeDocumentId,
      activeDocumentViewerTab,
      annotations,
      activeAnnotationIndex,
      searchQuery,
      searchLoading,
    },
    setDocumentPanelProps,
  } = useSelectedSuggestionEvidenceContext();

  const chatContext = useOptionalDocumentChatContext();
  const pdfViewerContext = useOptionalAnnotatedPDFViewerContext();

  useEffect(() => {
    chatContext?.clearChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only on mount to clear chat when modal opens
  }, []);

  useEffect(() => {
    chatContext?.setDocumentId(activeDocumentId);
    chatContext?.clearChat();
    pdfViewerContext?.setAnnotations(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset state when document changes, not when context refs change
  }, [activeDocumentId]);

  useResetEvidenceOnDPEntry(isDPSection);

  // Determine diagramPrimaryClientId: use selectedFirstDeathClientId for DP section if present (non-empty string),
  // otherwise default to the first primary client's id if available.
  const diagramPrimaryClientId: string | undefined = isDPSection
    ? selectedFirstDeathClientId || primaryClients?.[0]?.id
    : undefined;

  return (
    <FullScreenModal {...modalProps}>
      <SplitScreenLayoutWithDocuments
        householdId={householdId}
        targetId={testamentaryEntityId}
        header={<EditTestamentaryEntitySplitScreenHeader />}
        rightContent={<EditTestamentaryEntitySplitScreenRightContent />}
        rightPane={<DocumentChatPane documentId={activeDocumentId} />}
        footer={<EditTestamentaryEntitySplitScreenFooter />}
        overrideActiveDocumentId={activeDocumentId}
        onUpdateActiveDocumentId={({
          documentId,
          shouldClearSearchResults,
        }) => {
          chatContext?.setDocumentId(documentId);
          chatContext?.clearChat();

          if (shouldClearSearchResults) {
            // Clear out the annotations and search-related props
            setDocumentPanelProps((prev) => ({
              ...prev,
              activeDocumentId: documentId,
              searchQuery: '',
              annotations: undefined,
              activeAnnotationIndex: undefined,
              searchLoading: false,
            }));
            return;
          }
          // Otherwise, only update the document id
          setDocumentPanelProps((prev) => ({
            ...prev,
            activeDocumentId: documentId,
          }));
        }}
        overrideSearchQuery={searchQuery}
        overrideSearchLoading={searchLoading}
        overrideAnnotations={annotations}
        overrideActiveAnnotationIndex={activeAnnotationIndex}
        overrideDocumentViewerActiveTab={activeDocumentViewerTab}
        onUpdateDocumentViewerActiveTab={() => {
          // Unset the selected suggestion when the user switches between the document/summary views.
          // Preserve activeDocumentId so the chat pane remains functional.
          setDocumentPanelProps((prev) => ({
            ...prev,
            activeDocumentViewerTab: undefined,
            annotations: undefined,
            activeAnnotationIndex: undefined,
            searchQuery: '',
            searchLoading: false,
          }));
        }}
        initialPrimaryClientId={diagramPrimaryClientId}
        showDiagram={isDPSection}
      />
    </FullScreenModal>
  );
}

export function EditTestamentaryEntitySplitScreenModal({
  testamentaryEntityId,
  isTwoClientHousehold,
  initialSection,
  initialFirstDeathClientId,
  ...modalProps
}: EditTestamentaryEntitySplitScreenModalProps) {
  const footerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // A map of the duplicate refs for each array field. This only includes
  // array fields that _might_ have duplicates.
  // The refs need to be initialized upfront, because they need to be passed
  // to the subforms.
  const duplicatesRefs = useRef<
    Partial<
      Record<AISuggestionsMatcherArrayField, RefObject<HTMLDivElement | null>>
    >
  >({
    'trustDetailsSubform.trustees': useRef<HTMLDivElement | null>(null),
    'trustDetailsSubform.successorTrustees': useRef<HTMLDivElement | null>(
      null
    ),
    'trustDetailsSubform.trustAdvisors': useRef<HTMLDivElement | null>(null),
    'beneficiariesSubform.beneficiaries': useRef<HTMLDivElement | null>(null),
    'lifetimeBeneficiariesSubform.lifetimeBeneficiaries':
      useRef<HTMLDivElement | null>(null),
    'beneficiariesAfterBothGrantorsDie.beneficiaries':
      useRef<HTMLDivElement | null>(null),
  });

  // Provide the testamentary entity details context so that
  // useDispositiveProvisionsContextData can find the entity ID.
  // This is needed because the modal can be opened from contexts
  // where TestamentaryEntityDetailsProvider is not already set up.
  const testamentaryEntityDetailsValue = useMemo(
    () => ({
      entityId: testamentaryEntityId,
      setEntityId: noop,
      totalMarketValue: new Decimal(0),
      setTotalMarketValue: noop,
    }),
    [testamentaryEntityId]
  );

  return (
    <DocumentChatProvider>
      <HouseholdPermissionAwareDisabledProvider>
        <DispositiveDiagramSyncProvider>
          <DPFormRecipientTrackerProvider>
            <TestamentaryEntityDetailsContext.Provider
              value={testamentaryEntityDetailsValue}
            >
              <EditTestamentaryEntitySplitScreenModalProvider
                testamentaryEntityId={testamentaryEntityId}
                isTwoClientHousehold={isTwoClientHousehold}
                initialSection={initialSection}
                initialFirstDeathClientId={initialFirstDeathClientId}
                footerRef={footerRef}
                headerRef={headerRef}
                duplicatesRefs={duplicatesRefs.current}
                onClose={modalProps.onClose}
              >
                <SuggestionAcknowledgementProvider>
                  <SelectedSuggestionEvidenceProvider entityType={null}>
                    <TestamentaryEntitySuggestionsProvider>
                      <DispositiveProvisionSuggestionsProvider
                        targetId={testamentaryEntityId}
                      >
                        <EditTestamentaryEntitySplitScreenModalInner
                          {...modalProps}
                        />
                      </DispositiveProvisionSuggestionsProvider>
                    </TestamentaryEntitySuggestionsProvider>
                  </SelectedSuggestionEvidenceProvider>
                </SuggestionAcknowledgementProvider>
              </EditTestamentaryEntitySplitScreenModalProvider>
            </TestamentaryEntityDetailsContext.Provider>
          </DPFormRecipientTrackerProvider>
        </DispositiveDiagramSyncProvider>
      </HouseholdPermissionAwareDisabledProvider>
    </DocumentChatProvider>
  );
}
